import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { matchPath, useLocation, useNavigationType } from "react-router-dom";
import { changeApplicationModule, changeLayout } from "../../store/actions";
import { applicationModules } from "../../constants/layout";
import { getLoggedInUser } from "../../helpers/backend_helper";
import InspectionJourneyNav from "../InspectionJourney/InspectionJourneyNav";
import Header from "./Header";
import AdminSidebar from "./AdminSidebar";
import InspectionSidebar from "./InspectionSidebar";
import Footer from "./Footer";
import { HelpProvider } from "../Help/ContextualHelp";
import OnboardingProvider from "../onboarding/OnboardingProvider";
import {
  canAccessAdmin,
  isAdminPath,
  isInspectionPath,
} from "./navigation";
import "./AppLayout.scss";
import { userRoutes } from "../../routes/allRoutes";
import PiersAssistantProvider from "../Assistant/PiersAssistant";

export default function AppLayout({ children }) {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigationType = useNavigationType();
  const { pathname } = location;
  const navigationHistoryRef = useRef({ keys: [location.key], index: 0 });
  const [account, setAccount] = useState(null);
  const admin = canAccessAdmin(account) && isAdminPath(pathname, account);
  const inspection =
    !admin && isInspectionPath(pathname);
  const showJourney =
    /^\/(inspections|engagements|inspectionreviews|inspectionreview)(\/|$)/.test(
      pathname
    ) &&
    !["/inspections/overview", "/inspections/new", "/engagements/new"].includes(
      pathname
    ) &&
    !/^\/(inspections|engagements)\/[^/]+\/(edit|preview)$/.test(pathname);
  const inspectionCycleList = pathname === "/inspections/list";
  const fullScreenPage = userRoutes.some(
    (route) => route.layout === "full-screen" && matchPath(route.path, pathname)
  );
  const onboarding = pathname === "/piers/getting-started";
  const knownHistoryIndex = navigationHistoryRef.current.keys.indexOf(
    location.key
  );
  const isHistoryBack =
    navigationType === "POP" &&
    knownHistoryIndex >= 0 &&
    knownHistoryIndex < navigationHistoryRef.current.index;
  const isHistoryForward =
    navigationType === "POP" &&
    knownHistoryIndex > navigationHistoryRef.current.index;
  const slideBack = location.state?.inspectionSlide === "back" || isHistoryBack;
  const slideForward =
    !slideBack &&
    (location.state?.inspectionSlide === true ||
      location.state?.inspectionSlide === "forward" ||
      isHistoryForward);
  useEffect(() => {
    let mounted = true;
    getLoggedInUser().then((user) => {
      if (mounted) setAccount(user);
    });
    dispatch(changeLayout("vertical"));
    return () => {
      mounted = false;
    };
  }, [dispatch]);
  useEffect(() => {
    dispatch(
      changeApplicationModule(
        admin
          ? applicationModules.ADMINISTRATION
          : applicationModules.INSPECTION_WORKSPACE
      )
    );
    window.scrollTo(0, 0);
  }, [admin, dispatch, pathname]);
  useEffect(() => {
    const history = navigationHistoryRef.current;
    const existingIndex = history.keys.indexOf(location.key);
    if (navigationType === "POP" && existingIndex >= 0)
      history.index = existingIndex;
    else if (navigationType === "REPLACE")
      history.keys[history.index] = location.key;
    else if (navigationType === "PUSH") {
      history.keys = [
        ...history.keys.slice(0, history.index + 1),
        location.key,
      ];
      history.index += 1;
    }
  }, [location.key, navigationType]);
  return (
    <OnboardingProvider>
      <HelpProvider>
        <PiersAssistantProvider>
          <div className="loader-container">
            <div className="spinner" />
          </div>
          {fullScreenPage ? (
            children
          ) : (
            <div
              id="layout-wrapper"
              className={`piers-app-layout${
                admin ? " piers-app-layout--admin" : ""
              }${inspection ? " piers-app-layout--inspection" : ""}${
                onboarding ? " piers-app-layout--onboarding" : ""
              }`}
            >
              <Header />
              {admin && !onboarding && <AdminSidebar account={account} />}
              {inspection && !onboarding && <InspectionSidebar />}
              <div className="main-content">
                {showJourney &&
                  !inspectionCycleList &&
                  !/^\/inspections\/[^/]+\/engagements/.test(pathname) && (
                    <InspectionJourneyNav
                      guidanceVariant="inline"
                      stage={
                        pathname.startsWith("/inspectionreview") ||
                        pathname.startsWith("/review-tasks")
                          ? "reviews"
                          : pathname.startsWith("/engagements")
                          ? "engagements"
                          : "cycles"
                      }
                    />
                  )}
                <div
                  className={`piers-app-layout__page${
                    slideForward ? " piers-app-layout__page--slide-in" : ""
                  }${
                    slideBack ? " piers-app-layout__page--slide-back-in" : ""
                  }`}
                >
                  {children}
                </div>
                {!onboarding && <Footer />}
              </div>
            </div>
          )}
        </PiersAssistantProvider>
      </HelpProvider>
    </OnboardingProvider>
  );
}
